// store/features/enrichment.feature.tsx
import {
  BulkEnrichmentRequest,
  BulkEnrichmentResponse,
  EnrichmentSessionListResponse,
  EnrichmentSessionStatus,
  EnrichmentTaskResponse,
  EnrichmentTaskStatus,
  ManualProspectEnrichmentRequest,
  ProfileRefreshRequest,
  ProfileRefreshResponse,
  ReverseEmailSingleResponse,
} from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';
import { leadListsApi } from './lead-lists.feature';

export const enrichmentApi = createApi({
  reducerPath: 'enrichmentApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['EnrichmentTask', 'EnrichmentSession'],
  endpoints: (builder) => ({
    enrichProspectManually: builder.mutation<
      EnrichmentTaskResponse,
      ManualProspectEnrichmentRequest
    >({
      query: (payload) => ({
        url: '/enrichment/manual',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, __, arg) => [
        { type: 'EnrichmentTask', id: arg.lead_list_id },
      ],
    }),

    bulkEnrichProspects: builder.mutation<
      BulkEnrichmentResponse,
      BulkEnrichmentRequest
    >({
      query: (payload) => ({
        url: '/enrichment/bulk',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, _, arg) =>
        result
          ? [
              { type: 'EnrichmentSession', id: result.workflow_id },
              { type: 'EnrichmentSession', id: `list-${arg.lead_list_id}` },
            ]
          : [],
      // Invalidate lead list cache to get updated custom_column_config after import
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Short delay to ensure backend has committed the changes
          setTimeout(() => {
            dispatch(
              leadListsApi.util.invalidateTags([
                { type: 'LeadListLeads', id: arg.lead_list_id },
                { type: 'LeadList', id: arg.lead_list_id },
              ])
            );
          }, 500);
        } catch {
          // Mutation failed, no need to invalidate
        }
      },
    }),

    // Single-email reverse lookup. Synchronous (~40s); resolves one email to
    // a LinkedIn profile and, only on a match, imports a single lead into the
    // current list. No enrichment cascade. Charges 1 credit per attempt.
    reverseEmailSingle: builder.mutation<
      ReverseEmailSingleResponse,
      { email: string; lead_list_id: string }
    >({
      query: (payload) => ({
        url: '/enrichment/reverse-email-single',
        method: 'POST',
        body: payload,
      }),
      // Only an `imported` result changes the list, so only then refetch the
      // leads table. A short delay mirrors bulkEnrichProspects, giving the
      // backend a beat to commit before we invalidate.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.status === 'imported') {
            setTimeout(() => {
              dispatch(
                leadListsApi.util.invalidateTags([
                  { type: 'LeadListLeads', id: arg.lead_list_id },
                  { type: 'LeadList', id: arg.lead_list_id },
                ])
              );
            }, 500);
          }
        } catch {
          // Mutation failed, nothing to invalidate
        }
      },
    }),

    getEnrichmentTaskStatus: builder.query<EnrichmentTaskStatus, string>({
      query: (taskId) => `/enrichment/task/${taskId}/status`,
      providesTags: (_, __, taskId) => [{ type: 'EnrichmentTask', id: taskId }],
    }),

    getEnrichmentSessionStatus: builder.query<EnrichmentSessionStatus, string>({
      query: (workflowId) => `/enrichment/workflow/${workflowId}/status`,
      providesTags: (_, __, workflowId) => [
        { type: 'EnrichmentSession', id: workflowId },
      ],
    }),

    getEnrichmentSessions: builder.query<EnrichmentSessionListResponse, string>(
      {
        query: (leadListId) => `/enrichment/sessions/list/${leadListId}`,
        providesTags: (_, __, leadListId) => [
          { type: 'EnrichmentSession', id: `list-${leadListId}` },
        ],
      }
    ),

    // "Enrich Contact": refresh title / location / seniority / skills on
    // existing leads via a schema-only Crustdata resolve. Never touches
    // contact data. Replaces the prospecting_only dispatch, which was a
    // structural no-op on CSV leads (see column-config-panel).
    profileRefresh: builder.mutation<
      ProfileRefreshResponse,
      ProfileRefreshRequest
    >({
      query: (payload) => ({
        url: '/enrichment/profile-refresh',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, _, arg) =>
        result
          ? [
              { type: 'EnrichmentSession', id: result.workflow_id },
              { type: 'EnrichmentSession', id: `list-${arg.lead_list_id}` },
            ]
          : [],
    }),

    cancelBulkEnrichment: builder.mutation<
      { success: boolean; workflow_id: string },
      string
    >({
      query: (workflowId) => ({
        url: `/enrichment/workflow/${workflowId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, workflowId) => [
        { type: 'EnrichmentSession', id: workflowId },
      ],
    }),
  }),
});

export const {
  useEnrichProspectManuallyMutation,
  useBulkEnrichProspectsMutation,
  useReverseEmailSingleMutation,
  useCancelBulkEnrichmentMutation,
  useProfileRefreshMutation,
  useLazyGetEnrichmentTaskStatusQuery,
  useGetEnrichmentSessionStatusQuery,
  useLazyGetEnrichmentSessionStatusQuery,
  useGetEnrichmentSessionsQuery,
} = enrichmentApi;
